import { styled, useTheme } from '@automata/mui-theme';
import Box from '@mui/material/Box';
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getStraightPath,
} from 'reactflow';

import Triangle from './Triangle';
import { selectTaskDependenciesErrors } from '../../../features/selectors';
import { useAppSelector } from '../../../hooks';
import {
  DESELECTED_EDGE_STROKE_WIDTH,
  SELECTED_EDGE_STROKE_WIDTH,
} from '../constants';

type Props = EdgeProps & {};

type EdgeLabelInnerProps = {
  translateX: number;
  translateY: number;
};

const shouldForwardProp = (prop: string): boolean =>
  !['translateX', 'translateY'].includes(prop);

const EdgeLabelInner = styled(Box, { shouldForwardProp })<EdgeLabelInnerProps>`
  position: absolute;
  pointer-events: all;
  transform: translate(-50%, -50%)
    translate(${(p) => p.translateX}px, ${(p) => p.translateY}px);
`;

export const TaskDependencyEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  selected,
}: Props): JSX.Element => {
  const theme = useTheme();
  const taskDependencyErrors = useAppSelector(selectTaskDependenciesErrors)?.[
    id
  ];

  const isInvalid = (taskDependencyErrors?.length ?? 0) > 0;

  const [edgePath, labelX, labelY] = getStraightPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  const angleDegOffset = -90;
  const angle = Math.atan2(targetY - sourceY, targetX - sourceX);
  const angleDeg = (angle * 180) / Math.PI - angleDegOffset;

  const size = 25;

  const strokeWidth = selected
    ? SELECTED_EDGE_STROKE_WIDTH
    : DESELECTED_EDGE_STROKE_WIDTH;

  const stroke = isInvalid ? theme.palette.error.main : theme.palette.grey[400];

  return (
    <>
      <BaseEdge
        path={edgePath}
        style={{
          stroke,
          strokeWidth,
          strokeLinecap: 'round',
          transition: 'stroke-width 0.2s',
        }}
      />
      <EdgeLabelRenderer>
        <EdgeLabelInner translateX={labelX} translateY={labelY}>
          <div style={{ transform: `rotate(${angleDeg}deg)` }}>
            <div
              style={{
                transform: `translateY(${selected ? 0 : 5}px)`,
                transition: 'transform 0.2s 0.1s',
              }}
            >
              <Triangle
                size={size}
                stroke={stroke}
                strokeWidth={strokeWidth}
                style={{ transition: 'stroke-width 0.2s' }}
              />
            </div>
          </div>
        </EdgeLabelInner>
      </EdgeLabelRenderer>
    </>
  );
};

export default TaskDependencyEdge;
